'use client'

import { LineApps } from '@qr-front/icons/line-apps'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { useAtom, useAtomValue } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { PrimaryAndSecondaryColorFields } from '@/components/PrimaryAndSecondaryColorFields'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { TGeneratorFormSection } from '../../components/GeneratorFormSection'
import {
	FRAME_BLACK,
	TRANSPARENT_COLOR,
} from '../../qrFrames/qrFramesConstants'
import type { TQrPattern } from '../../services/types'
import {
	ClassyPatternIcon,
	ClassyRoundedPatternIcon,
	DotsPatternIcon,
	ExtraRoundedPatternIcon,
	RoundedPatternIcon,
	SquarePatternIcon,
} from './qrPatternIcons'
import { QrStyleField } from './QrStyleField'

const PATTERN_OPTION_LIST = (t: TTranslationFunction) => [
	{
		value: 'rounded',
		Preview: RoundedPatternIcon,
		tooltip: t('generator__design_form__dot_pattern_tooltip'),
	},
	{
		value: 'dots',
		Preview: DotsPatternIcon,
		tooltip: t('generator__design_form__dots_pattern_tooltip'),
	},
	{
		value: 'classy',
		Preview: ClassyPatternIcon,
		tooltip: t('generator__design_form__classy_pattern_tooltip'),
	},
	{
		value: 'classy-rounded',
		Preview: ClassyRoundedPatternIcon,
		tooltip: t('generator__design_form__classy_rounded_pattern_tooltip'),
	},
	{
		value: 'square',
		Preview: SquarePatternIcon,
		tooltip: t('generator__design_form__square_pattern_tooltip'),
	},
	{
		value: 'extra-rounded',
		Preview: ExtraRoundedPatternIcon,
		tooltip: t('generator__design_form__extra_rounded_pattern_tooltip'),
	},
]

export const DEFAULT_PATTERN = 'square'
const FALLBACK_QR_BACKGROUND_COLOR = '#FFFFFF'

export const patternAtom = atomWithReset<TQrPattern>(DEFAULT_PATTERN)
export const qrDotColorAtom = atomWithReset<string>(FRAME_BLACK)
export const qrBackgroundColorAtom = atomWithReset<string>(
	FALLBACK_QR_BACKGROUND_COLOR,
)

const PatternField = () => {
	const [selectedOption, setSelectedOption] = useAtom(patternAtom)
	const t = useTranslations()

	const dotColor = useAtomValue(qrDotColorAtom)
	const qrBackgroundColor = useAtomValue(qrBackgroundColorAtom)

	const patternOptionList = useMemo(() => PATTERN_OPTION_LIST(t), [t])

	return (
		<QrStyleField
			value={selectedOption === undefined ? 'none' : selectedOption}
			setValue={(value) => setSelectedOption(value as TQrPattern)}
			optionList={patternOptionList}
			designElement="pattern"
			color={dotColor}
			backgroundColor={qrBackgroundColor}
		/>
	)
}

export const PatternDesignForm = () => {
	const t = useTranslations()

	const [dotColor, setDotColor] = useAtom(qrDotColorAtom)
	const [backgroundColor, setBackgroundColor] = useAtom(qrBackgroundColorAtom)
	const isBackgroundTransparent = backgroundColor === TRANSPARENT_COLOR

	const [savedBackgroundColor, setSavedBackgroundColor] = useState(() => {
		const initialBackgroundColor = getStore().get(qrBackgroundColorAtom)
		return initialBackgroundColor === TRANSPARENT_COLOR
			? FALLBACK_QR_BACKGROUND_COLOR
			: initialBackgroundColor
	})

	return (
		<div className="flex flex-col gap-6 tablet:gap-8">
			<h4 className="type-heading-4">
				{t('generator__design_form__pattern_section__heading')}
			</h4>
			<div className="flex flex-col gap-6 desktop:flex-row desktop:gap-18">
				<PatternField />
			</div>
			<div className="rounded-10 bg-light-grey-70 p-4 tablet:p-6">
				<PrimaryAndSecondaryColorFields
					primaryColor={dotColor}
					setPrimaryColor={setDotColor}
					primaryLabel={t(
						'generator__design_form__pattern_section__dot_label',
					)}
					secondaryColor={backgroundColor}
					setSecondaryColor={setBackgroundColor}
					secondaryLabel={t(
						'generator__design_form__pattern_section__background_label',
					)}
					isSecondaryDisabled={isBackgroundTransparent}
				/>
				<Checkbox
					checked={isBackgroundTransparent}
					onCheckedChange={(checked) => {
						if (checked) {
							setSavedBackgroundColor(
								backgroundColor || FALLBACK_QR_BACKGROUND_COLOR,
							)
						}
						setBackgroundColor(
							checked
								? TRANSPARENT_COLOR
								: savedBackgroundColor ||
										FALLBACK_QR_BACKGROUND_COLOR,
						)
					}}
					id="qr-transparency"
					label={t(
						'generator__design_form__pattern_section__transparent_background',
					)}
					className="mt-4 tablet:mt-8"
				/>
			</div>
		</div>
	)
}

export const usePatternDesignForm = (): TGeneratorFormSection => {
	const t = useTranslations()

	return {
		id: 'pattern',
		title: t('generator__design_form__pattern_section__title'),
		description: t(
			'generator__design_form__pattern_section__description__changed',
		),
		// TODO: Temporary key. After deleting the old `...__description` from Lokalise, rename this __changed key back to `...__description`.
		icon: <LineApps />,
		Content: PatternDesignForm,
	}
}
